import dayjs from 'dayjs';
import { Box, Typography, ButtonGroup, Button } from '@mui/material';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import SearchableSelector from 'components/SearchableSelector';
import { useAppDispatch } from 'store/hook';
import {
  clearNotification,
  type NotificationState,
  showNotification,
} from 'store/notification.slice';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import useAppTheme from 'hooks/useAppTheme';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const dateRangeList = ['1M', '3M', '1Y', 'YTD', 'Custom'];

export const Filter = ({
  assets,
  selectedAsset,
  setSelectedAsset,
  categories,
  selectedPart,
  setSelectedPart,
  handleDateChange,
  selectedStartDate,
  selectedEndDate,
  isSelected,
  setIsSelected,
  setStartDate,
  setEndDate,
}: {
  assets: string[];
  selectedAsset: string[];
  setSelectedAsset: any;
  categories: any[];
  selectedPart: string[];
  setSelectedPart: any;
  handleDateChange: any;
  selectedStartDate: any;
  selectedEndDate: any;
  isSelected: string;
  setIsSelected: any;
  setStartDate: any;
  setEndDate: any;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const squaresHandler = new SquaresDashboardHandler();

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const handleSelect = (id: string) => {
    setIsSelected(id);
    let newStartDate = new Date();
    const newEndDate = new Date();

    switch (id) {
      case '1M':
        newStartDate = dayjs(newStartDate).subtract(31, 'day').toDate();
        break;
      case '3M':
        newStartDate = dayjs(newStartDate).subtract(91, 'days').toDate();
        break;
      case '1Y':
        newStartDate = dayjs(newStartDate).subtract(1, 'year').toDate();
        break;
      case 'YTD':
        newStartDate = dayjs(newStartDate).startOf('year').toDate();
        break;
      case 'Custom':
        break;
      default:
        break;
    }

    if (id !== 'Custom') {
      // for quick ranfge call
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      void squaresHandler.getPartsRecommendations(newStartDate, newEndDate);
    }
  };

  const handlePartsSelection = (values: any) => {
    if (Array.isArray(values)) {
      if (values.length > 10) {
        dispatch(
          showNotification({
            message: t('please_select_up_to_10_parts'),
            type: 'error',
          })
        );
        setTimeout(() => {
          dispatch(clearNotification());
        }, 3000);
      } else {
        setSelectedPart(values);
      }
    } else {
      setSelectedPart([]);
    }
  };

  const handleApplyClick = () => {
    if (selectedStartDate && selectedEndDate) {
      dispatch(clearNotification());
      void squaresHandler.getPartsRecommendations(
        selectedStartDate,
        selectedEndDate
      );
    } else {
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: `Invalid date selected, please select a valid range.`,
        show: true,
        type: 'error',
      };
      dispatch(showNotification(notificationPayload));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        marginTop: '10px',
        marginBottom: '20px',
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        padding: '15px 10px 15px 10px',
        borderRadius: '5px',
      }}
      flexDirection={'column'}
      gap={2}
    >
      <Box display={'flex'} alignItems={'center'}>
        <Typography
          style={{
            fontSize: '12px',
            marginRight: '10px',
            color: palette.textPrimary,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {t('quick_range_10')}
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ButtonGroup
            size="small"
            variant="outlined"
            aria-label={t('basic_button_group')}
            sx={{
              border: '1px solid #2F445D',
              marginRight: '10px',
            }}
          >
            {dateRangeList.map((item) => {
              return (
                <Button
                  key={item}
                  size="small"
                  style={{
                    backgroundColor:
                      isSelected === item
                        ? palette.primary
                        : enableDarkTheme
                        ? palette.cartesianGrid
                        : palette.boxBackground,
                    color:
                      isSelected === item ? palette.white : palette.textPrimary,
                  }}
                  onClick={() => {
                    handleSelect(item);
                  }}
                >
                  {item}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>
        <Typography
          sx={{
            color: palette.textPrimary,
            marginRight: '10px',
          }}
        >
          {t('date_range_10')}
        </Typography>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CustomDateRangePicker
            // onChange={handleDateChange}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            maxDate={dayjs(new Date().getTime())}
            disabled={isSelected !== 'Custom'}
            wrap
            showDash={true}
            autoOpenEnabled={true}
            sx={{
              width: '100px',
            }}
            onFinalDateChange={handleDateChange}
            // views={['year', 'month', 'day']}
          />
          <div style={{ width: '64px', height: '30px' }}>
            {isSelected === 'Custom' && (
              <CustomLoadingButton
                onClick={handleApplyClick}
                sx={{
                  fontSize: '12px',
                  height: '30px',
                }}
              >
                {t('apply')}
              </CustomLoadingButton>
            )}
          </div>
        </div>
      </Box>

      <Box display={'flex'} gap={2}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('select_assets_2')}
          </Typography>
          {/** Assets Selector */}
          <SearchableSelector
            options={assets}
            selectedValues={selectedAsset}
            onSelectionChange={setSelectedAsset}
            placeholder={t('search_assets_2')}
            label={t('asset')}
            // enableDarkTheme={enableDarkTheme}
            // sx={{ marginLeft: '10px', marginRight: '20px' }}
            multiple={true}
            showSelectAll={true}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/** Parts Selector */}
          <Typography
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('select_parts')}
          </Typography>
          <SearchableSelector
            options={categories}
            selectedValues={selectedPart}
            onSelectionChange={handlePartsSelection}
            placeholder={t('search_parts')}
            label={t('part')}
            // enableDarkTheme={enableDarkTheme}
            // sx={{ marginLeft: '10px', marginRight: '20px' }}
            multiple={true}
            showSelectAll={false}
          />
        </div>
      </Box>
    </Box>
  );
};
